import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import CheckIcon from '@material-ui/icons/Check';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import { WbBadge, WbButton, WbTooltip } from '@agilelab/plugin-wb-platform';
import { ResponseErrorPanel } from '@mainstage/core-components';
import useAsync from 'react-use/lib/useAsync';
import {
  Background,
  BackgroundVariant,
  BaseEdge,
  Edge,
  EdgeProps,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  Node,
  NodeProps,
  NodeChange,
  Panel,
  Position,
  ReactFlow,
  useNodesInitialized,
  useReactFlow,
} from 'reactflow';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildWorkflowGraphLayout,
  graphNodeHeight,
  graphNodeWidth,
  WorkflowGraphPort,
  WorkflowGraphRect,
} from './workflowDefinitionGraphLayout';
import {
  getLifecycleMacroPhaseColor,
  LifecycleMacroPhaseColor,
} from './lifecycleMacroPhaseColors';

const dimmedOpacity = 0.25;

const useStyles = makeStyles(theme => ({
  node: {
    width: graphNodeWidth,
    minHeight: graphNodeHeight,
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(0.75),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
  },
  selectedNode: {
    outline: `2px solid ${theme.palette.accent.main}`,
    outlineOffset: 4,
  },
  nodeTitleText: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5, 2),
    margin: theme.spacing(2),
    maxWidth: 'calc(100% - 88px)',
    backgroundColor: theme.palette.background.paper,
  },
  phaseFilter: {
    margin: 0,
    gap: theme.spacing(0.75),
  },
  phaseFilterLabel: {
    color: theme.palette.text.primary,
    fontSize: 12,
  },
  phaseCheckbox: {
    padding: 0,
  },
  phaseCheckboxIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: 16,
    height: 16,
    border: '1px solid',
    borderRadius: 2,
  },
  controls: {
    margin: theme.spacing(2),
  },
  controlsPaper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 40,
    boxSizing: 'border-box',
    padding: theme.spacing(0.75, 0),
    border: `1px solid ${theme.palette.blueGrey[100]}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.white,
    boxShadow: theme.shadows[1],
  },
  controlButton: {
    minWidth: 32,
    minHeight: 32,
    padding: theme.spacing(0.5),
    '& [class*="MuiSvgIcon-root"]': {
      fontSize: 16,
    },
  },
  transitionTag: {
    position: 'absolute',
    left: 0,
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'all',
    '&&': {
      height: 20,
      minHeight: 20,
      fontSize: 10,
      borderStyle: 'dashed',
    },
    '& > span': {
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
  },
}));

export interface WorkflowDefinitionGraphStage {
  id: string;
  label?: string;
  description?: string;
  icon?: string;
  macroPhase?: string;
}

export interface WorkflowDefinitionGraphTransition {
  id?: string;
  fromStage: string | null;
  toStage: string;
  label?: string;
  when?: Record<string, unknown>;
  show?: boolean;
  featured?: boolean;
  requirements?: Array<{ type: 'trigger' | 'precondition'; label: string }>;
}

interface GraphCard {
  id: string;
  title: string;
  description?: string;
  color: LifecycleMacroPhaseColor;
  isCurrent: boolean;
  isSelected: boolean;
  ports: WorkflowGraphPort[];
}

function GraphNode({ data }: NodeProps<{ card: GraphCard }>) {
  const { card } = data;
  const classes = useStyles();
  const theme = useTheme();
  const hiddenHandle = {
    opacity: 0,
    border: 'none',
    background: 'transparent',
  };
  return (
    <>
      {card.ports.map(port => (
        <Handle
          key={port.id}
          id={port.id}
          type={port.type}
          position={
            {
              top: Position.Top,
              bottom: Position.Bottom,
              left: Position.Left,
              right: Position.Right,
            }[port.position]
          }
          style={{
            ...hiddenHandle,
            width: 1,
            height: 1,
            left: port.x,
            top: port.y,
          }}
        />
      ))}
      <Box
        className={[
          classes.node,
          card.isCurrent || card.isSelected ? classes.selectedNode : '',
        ].join(' ')}
        style={{
          backgroundColor: theme.palette.bkg[card.color],
          borderColor: theme.palette[card.color].dark,
        }}
      >
        <Typography variant="subtitle2" className={classes.nodeTitleText}>
          {card.title}
        </Typography>
        {card.description && (
          <Typography variant="body1">{card.description}</Typography>
        )}
      </Box>
    </>
  );
}

function BoundaryNode(): null {
  return null;
}

function GraphControls({ visibleNodeIds }: { visibleNodeIds: string[] }) {
  const classes = useStyles();
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  return (
    <Panel position="top-right" className={classes.controls}>
      <Paper className={classes.controlsPaper}>
        <WbTooltip title="Fit to View" placement="left">
          <WbButton
            icon={<FullscreenIcon />}
            variant="text"
            className={classes.controlButton}
            aria-label="Fit to View"
            disabled={visibleNodeIds.length === 0}
            onClick={() =>
              fitView({
                nodes: visibleNodeIds.map(id => ({ id })),
                padding: 0.3,
                duration: 400,
              })
            }
          />
        </WbTooltip>
        <WbTooltip title="Zoom In" placement="left">
          <WbButton
            icon={<ZoomInIcon />}
            variant="text"
            className={classes.controlButton}
            aria-label="Zoom In"
            onClick={() => zoomIn({ duration: 400 })}
          />
        </WbTooltip>
        <WbTooltip title="Zoom Out" placement="left">
          <WbButton
            icon={<ZoomOutIcon />}
            variant="text"
            className={classes.controlButton}
            aria-label="Zoom Out"
            onClick={() => zoomOut({ duration: 400 })}
          />
        </WbTooltip>
      </Paper>
    </Panel>
  );
}

function InitialViewport({
  currentStage,
  ready,
}: {
  currentStage?: string;
  ready: boolean;
}): null {
  const initialized = useNodesInitialized();
  const { fitView, getNode, getViewport, setViewport } = useReactFlow();
  const centered = useRef(false);
  useEffect(() => {
    if (!ready || !initialized || centered.current) return;
    centered.current = true;
    const currentNode = currentStage ? getNode(currentStage) : undefined;
    fitView({
      ...(currentNode ? { nodes: [{ id: currentNode.id }] } : {}),
      padding: 0.3,
      minZoom: 0.25,
      maxZoom: 0.9,
    });
    if (currentNode) {
      const viewport = getViewport();
      setViewport({
        ...viewport,
        y: 48 - currentNode.position.y * viewport.zoom,
      });
    }
  }, [
    ready,
    initialized,
    currentStage,
    fitView,
    getNode,
    getViewport,
    setViewport,
  ]);
  return null;
}

const nodeTypes = { lifecycle: GraphNode, boundary: BoundaryNode };

function BranchEdge({
  markerEnd,
  style,
  data,
}: EdgeProps<{
  onSelect?: () => void;
  points: Array<{ x: number; y: number }>;
  opacity: number;
  label?: string;
  labelBounds?: WorkflowGraphRect;
}>) {
  const classes = useStyles();
  const path =
    data?.points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ') ?? '';
  return (
    <>
      <BaseEdge path={path} markerEnd={markerEnd} style={style} />
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={16}
        style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
        onClick={data?.onSelect}
      />
      {data?.label && data.labelBounds && (
        <EdgeLabelRenderer>
          <WbTooltip title={data.label}>
            <WbBadge
              color="secondary"
              variant="stroke"
              className={`nodrag nopan ${classes.transitionTag}`}
              aria-label={`Transition: ${data.label}`}
              style={{
                opacity: data.opacity,
                width: data.labelBounds.width,
                transform: `translate(${data.labelBounds.x}px, ${data.labelBounds.y}px)`,
              }}
              onClick={event => {
                event.stopPropagation();
                data.onSelect?.();
              }}
            >
              {data.label}
            </WbBadge>
          </WbTooltip>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const edgeTypes = { branch: BranchEdge };

export function WorkflowDefinitionGraph({
  stages,
  macroPhases = [],
  transitions,
  currentStage,
  visitedStages: _visitedStages = new Set<string>(),
  selectedStageId,
  selectedTransitionId,
  onStageSelect,
  onTransitionSelect,
}: {
  stages: WorkflowDefinitionGraphStage[];
  macroPhases?: Array<{ id: string; label?: string }>;
  transitions: WorkflowDefinitionGraphTransition[];
  currentStage?: string;
  visitedStages?: Set<string>;
  selectedStageId?: string;
  selectedTransitionId?: string;
  onStageSelect?: (stageId: string) => void;
  onTransitionSelect?: (transitionId: string) => void;
}): JSX.Element {
  const classes = useStyles();
  const theme = useTheme();
  const [nodeHeights, setNodeHeights] = useState<Record<string, number>>({});
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodeHeights(previous => {
      let next = previous;
      for (const change of changes) {
        if (
          change.type === 'dimensions' &&
          !change.id.startsWith('__') &&
          change.dimensions &&
          previous[change.id] !== change.dimensions.height
        ) {
          next = { ...next, [change.id]: change.dimensions.height };
        }
      }
      return next;
    });
  }, []);
  const [dimmedMacroPhases, setDimmedMacroPhases] = useState<Set<string>>(
    new Set(),
  );
  const dimmedStageIds = new Set(
    stages
      .filter(
        stage => stage.macroPhase && dimmedMacroPhases.has(stage.macroPhase),
      )
      .map(stage => stage.id),
  );
  const visibleTransitions = transitions.filter(
    transition => transition.show !== false,
  );
  const layoutStages = stages.map(stage => ({
    id: stage.id,
    label: stage.label,
    macroPhase: stage.macroPhase,
    height: nodeHeights[stage.id],
  }));
  const layoutKey = JSON.stringify({
    stages: layoutStages,
    macroPhases,
    transitions: visibleTransitions,
  });
  const {
    value: layout,
    loading,
    error,
  } = useAsync(
    () =>
      buildWorkflowGraphLayout(layoutStages, macroPhases, visibleTransitions),
    [layoutKey],
  );
  const layoutNodes = new Map(layout?.nodes.map(node => [node.id, node]));
  const cards: GraphCard[] = stages.map(stage => ({
    id: stage.id,
    title: stage.label ?? stage.id,
    description: stage.description,
    color: getLifecycleMacroPhaseColor(
      macroPhases.findIndex(phase => phase.id === stage.macroPhase),
    ),
    isCurrent: stage.id === currentStage,
    isSelected: stage.id === selectedStageId,
    ports: layoutNodes.get(stage.id)?.ports ?? [],
  }));
  const stageNodes: Node[] = cards.flatMap(card => {
    const node = layoutNodes.get(card.id);
    if (!node) return [];
    return [
      {
        id: card.id,
        position: { x: node.x, y: node.y },
        type: 'lifecycle',
        data: { card },
        draggable: false,
        selectable: false,
        style: {
          opacity: dimmedStageIds.has(card.id) ? dimmedOpacity : 1,
          width: graphNodeWidth,
          border: 'none',
          background: 'transparent',
          zIndex: 2,
        },
      },
    ];
  });
  const edges: Edge[] = (layout?.edges ?? []).map(edge => {
    const highlighted = edge.id === selectedTransitionId;
    const opacity =
      dimmedStageIds.has(edge.source) || dimmedStageIds.has(edge.target)
        ? dimmedOpacity
        : 1;
    const color = highlighted
      ? theme.palette.accent.main
      : theme.palette.secondary.main;
    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: 'branch',
      data: {
        opacity,
        onSelect: () => onTransitionSelect?.(edge.id),
        points: edge.points,
        label: edge.label,
        labelBounds: edge.labelBounds,
      },
      markerEnd: { type: MarkerType.ArrowClosed, color },
      style: {
        stroke: color,
        strokeWidth: highlighted ? 2.5 : 1.4,
        opacity: (highlighted ? 1 : 0.62) * opacity,
        strokeDasharray: highlighted ? undefined : '2 3',
        strokeLinejoin: 'round',
      },
      animated: false,
    };
  });
  const visibleBounds: WorkflowGraphRect[] = [
    ...(layout?.nodes ?? []),
    ...(layout?.edges ?? []).flatMap(edge => [
      edge.labelBounds,
      ...edge.points.map(point => ({ ...point, width: 0, height: 0 })),
    ]),
  ];
  const boundaryNodes: Node[] =
    visibleBounds.length === 0
      ? []
      : [
          {
            id: '__routing-start__',
            type: 'boundary',
            position: {
              x: Math.min(...visibleBounds.map(bounds => bounds.x)) - 16,
              y: Math.min(...visibleBounds.map(bounds => bounds.y)) - 16,
            },
            data: {},
            draggable: false,
            selectable: false,
            connectable: false,
            style: { width: 1, height: 1, opacity: 0 },
          },
          {
            id: '__routing-end__',
            type: 'boundary',
            position: {
              x:
                Math.max(
                  ...visibleBounds.map(bounds => bounds.x + bounds.width),
                ) + 16,
              y:
                Math.max(
                  ...visibleBounds.map(bounds => bounds.y + bounds.height),
                ) + 16,
            },
            data: {},
            draggable: false,
            selectable: false,
            connectable: false,
            style: { width: 1, height: 1, opacity: 0 },
          },
        ];
  const nodes: Node[] = [...stageNodes, ...boundaryNodes];
  if (error) return <ResponseErrorPanel error={error} />;
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodesChange={onNodesChange}
      onNodeClick={(_, node) => {
        if (node.type === 'lifecycle') onStageSelect?.(node.id);
      }}
      onEdgeClick={(_, edge) => onTransitionSelect?.(edge.id)}
      minZoom={0.25}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      panOnDrag
      zoomOnScroll
      proOptions={{ hideAttribution: true }}
    >
      <InitialViewport
        currentStage={currentStage}
        ready={
          !loading && stages.every(stage => nodeHeights[stage.id] !== undefined)
        }
      />
      {loading && (
        <Panel position="bottom-center">
          <CircularProgress size={20} aria-label="Arranging workflow chart" />
        </Panel>
      )}
      {macroPhases.length > 0 && (
        <Panel position="top-left" className={classes.filters}>
          {macroPhases.map((phase, index) => {
            const color = getLifecycleMacroPhaseColor(index);
            return (
              <FormControlLabel
                key={phase.id}
                className={classes.phaseFilter}
                classes={{ label: classes.phaseFilterLabel }}
                label={phase.label ?? phase.id}
                control={
                  <Checkbox
                    className={classes.phaseCheckbox}
                    icon={
                      <Box
                        className={classes.phaseCheckboxIcon}
                        style={{ borderColor: theme.palette[color].dark }}
                      />
                    }
                    checkedIcon={
                      <Box
                        className={classes.phaseCheckboxIcon}
                        style={{
                          borderColor: theme.palette[color].dark,
                          backgroundColor: theme.palette.bkg[color],
                        }}
                      >
                        <CheckIcon
                          style={{ fontSize: 14, color: theme.palette.black }}
                        />
                      </Box>
                    }
                    checked={!dimmedMacroPhases.has(phase.id)}
                    onChange={(_, checked) =>
                      setDimmedMacroPhases(previous => {
                        const next = new Set(previous);
                        if (checked) next.delete(phase.id);
                        else next.add(phase.id);
                        return next;
                      })
                    }
                  />
                }
              />
            );
          })}
        </Panel>
      )}
      <GraphControls
        visibleNodeIds={[...stageNodes, ...boundaryNodes].map(node => node.id)}
      />
      <Background
        color={theme.palette.grey[300]}
        gap={15}
        variant={BackgroundVariant.Cross}
      />
    </ReactFlow>
  );
}
